import { Component, effect, HostListener, output, signal } from '@angular/core';

/**
 * Componente per la gestione del Drag and Drop di file a livello di finestra o container.
 * * @description
 * Mostra un overlay sul container del parent quando l'utente trascina dei file.
 * l'evento sullo show del overlay viene gestito da window, quindi quando sento entrare un file dentro la pagina web, mostro l'overlay del drop
 * Gestisce automaticamente il flickering causato dal passaggio sopra elementi figli tramite un contatore.
 * * ### Requisiti CSS del Container
 * Per garantire che l'overlay copra correttamente l'area desiderata, il componente padre deve avere:
 * ```css
 * .parent-container {
 * position: relative;
 * overflow: hidden;
 * }
 * ```
 * * @usageNotes
 * ```html
 * <app-drag-drop-file-uploader (dropped)="onDroppedFile($event)" />
 * ```
 */
@Component({
  selector: 'app-drag-drop-file-uploader',
  standalone: false,

  templateUrl: './drag-drop-file-uploader.component.html',
  styleUrl: './drag-drop-file-uploader.component.scss',
})
export class DragDropFileUploaderComponent {
  /** * Evento emesso quando i file vengono rilasciati correttamente nell'area di drop.
   * Emette un array di oggetti `File`.
   */
  dropped = output<File[]>();

  draggingChanged = output<boolean>();

  /** * Stato reattivo che indica se è in corso un'operazione di trascinamento.
   * Utilizzato nel template per mostrare/nascondere l'overlay tramite `@if`.
   */
  isDragging = signal(false);

  /** * Contatore interno per gestire gli eventi dragenter/dragleave.
   * Impedisce la chiusura prematura dell'overlay quando il mouse passa sopra testi o icone.
   */
  private dragCounter = 0;

  constructor() {
    // Se sto draggando, mando evento al padre
    effect(() => {
      this.draggingChanged.emit(this.isDragging());
    });
  }

  /**
   * Gestisce l'ingresso del file nella finestra del browser.
   * Incrementa il contatore e attiva l'overlay.
   * @param event L'evento DragEvent del browser.
   */
  @HostListener('window:dragenter', ['$event'])
  onDragEnter(event: DragEvent) {
    event.preventDefault();
    this.dragCounter++;
    this.isDragging.set(true);
  }

  /**
   * Gestisce l'uscita del file o l'annullamento del trascinamento.
   * Decrementa il contatore e, se arriva a zero, nasconde l'overlay.
   * @param event L'evento DragEvent del browser.
   */
  @HostListener('window:dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragCounter--;

    if (this.dragCounter <= 0) {
      this.resetDrag();
    }
  }

  /**
   * Previene il comportamento di default del browser (apertura del file)
   * per permettere l'operazione di drop.
   * @param event L'evento DragEvent del browser.
   */
  @HostListener('window:dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault(); // Necessario per il drop
  }

  /**
   * Gestisce il rilascio dei file a livello di finestra per resettare lo stato.
   * Impedisce al browser di caricare il file se il drop avviene fuori dalla zona designata.
   * @param event L'evento DragEvent del browser.
   */
  @HostListener('window:drop', ['$event'])
  onWindowDrop(event: DragEvent) {
    event.preventDefault();
    this.resetDrag();
  }

  /**
   * Metodo richiamato dalla direttiva interna quando il drop avviene sull'area corretta.
   * Converte i file in un array ed emette l'evento finale.
   * @param files Oggetto FileList catturato dalla direttiva.
   */
  onFileDropped(files: FileList) {
    this.resetDrag();
    // Convertiamo FileList in un array di file per importazione BE
    const fileArray = Array.from(files);
    this.dropped.emit(fileArray);
  }

  /**
   * Resetta il contatore e lo stato di visibilità dell'interfaccia.
   * @private
   */
  private resetDrag() {
    this.dragCounter = 0;
    this.isDragging.set(false);
  }
}
